import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { useDialogContext } from "./internal/useDialogContext";
import { DataTrackingId } from "../../types";
import { useTrackingId } from "../../hooks";

/**
 * Props for the DialogCancelButton component
 * @extends ButtonProps
 */
export type DialogCancelButtonProps = ButtonProps & DataTrackingId;

/**
 * DialogCancelButton component for cancel/close actions in a Dialog.
 *
 * Features:
 * - Button that automatically closes the dialog when clicked
 * - Extends all Button component props and functionality
 * - Proper accessibility with close dialog semantics
 * - Automatic focus management
 * - Consistent styling with other dialog elements
 *
 * @example
 * <Dialog.CancelButton appearance="ghost">Cancel</Dialog.CancelButton>
 */
export const DialogCancelButton = forwardRef<
  HTMLButtonElement,
  DialogCancelButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { close } = useDialogContext();
  const data = {
    title: props.title,
    appearance: props.appearance,
    size: props.size,
    type: props.type,
  };

  const trackingId = useTrackingId({
    name: "DialogCancelButton",
    data,
    hasOverride: !!props["data-tracking-id"],
  });
  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    close();
  };
  return (
    <Button
      aria-label="Close dialog"
      data-anv="dialog-cancel-button"
      data-tracking-id={trackingId}
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DialogCancelButton.displayName = "DialogCancelButton";
